import { createTheme } from "@mui/material/styles";
import Colors from "src/general/constants/colors";
import BreakPoints from "src/general/constants/breakPoints";
import { TypographyOptions } from "@mui/material/styles/createTypography";

export const newLightStyles = createTheme({
	typography: {
		fontFamily: "Montserrat !important",
		h1: {
			fontSize: "32px",
			fontWeight: 600,
			lineHeight: "150%",
			letterSpacing: "0%",
			[`@media (max-width: ${BreakPoints.MOBILE_767}px)`]: {
				fontSize: "24px",
				fontWeight: 500,
				lineHeight: "114.99999999999999%",
				letterSpacing: "0px",
			},
		},
		h2: {
			fontSize: "24px",
			fontWeight: 500,
			lineHeight: "114.99999999999999%",
			letterSpacing: "0px",
			[`@media (max-width: ${BreakPoints.MOBILE_767}px)`]: {
				fontSize: "20px",
			},
		},
		h3: {
			fontSize: "20px",
			fontWeight: 500,
			lineHeight: "140%",
			letterSpacing: "0px",
			[`@media (max-width: ${BreakPoints.MOBILE_767}px)`]: {
				fontSize: "18px",
				lineHeight: "110%",
			},
		},
		h4: {
			fontSize: "18px",
			fontWeight: 500,
			lineHeight: "130%",
			letterSpacing: "0px",
			[`@media (max-width: ${BreakPoints.MOBILE_767}px)`]: {
				fontSize: "16px",
				lineHeight: "100%",
			},
		},
		subtitle1: {
			fontSize: "16px",
			fontWeight: 500,
			lineHeight: "140%",
			letterSpacing: "0px",
			[`@media (max-width: ${BreakPoints.MOBILE_767}px)`]: {
				fontSize: "14px",
			},
		},
		subtitle2: {
			fontSize: "16px",
			fontWeight: 400,
			lineHeight: "140%",
			letterSpacing: "0px",
			[`@media (max-width: ${BreakPoints.MOBILE_767}px)`]: {
				fontSize: "14px",
			},
		},
		body1: {
			fontSize: "14px",
			fontWeight: 400,
			lineHeight: "150%",
			letterSpacing: "0px",
			[`@media (max-width: ${BreakPoints.MOBILE_767}px)`]: {
				fontSize: "13px",
			},
		},
		body2: {
			fontSize: "14px",
			fontWeight: 500,
			lineHeight: "150%",
			letterSpacing: "0px",
			[`@media (max-width: ${BreakPoints.MOBILE_767}px)`]: {
				fontSize: "13px",
			},
		},
		body3: {
			fontSize: "13px",
			fontWeight: 400,
			lineHeight: "150%",
			letterSpacing: "0px",
			[`@media (max-width: ${BreakPoints.MOBILE_767}px)`]: {
				fontSize: "12px",
				fontWeight: 400,
			},
		},
		caption: {
			fontSize: "12px",
			fontWeight: 500,
			lineHeight: "150%",
			letterSpacing: "0px",
			[`@media (max-width: ${BreakPoints.MOBILE_767}px)`]: {
				fontSize: "10px",
			},
		},
		overline: {
			fontSize: "12px",
			fontWeight: 500,
			lineHeight: "100%",
			letterSpacing: "1px",
			textTransform: "uppercase",
			[`@media (max-width: ${BreakPoints.MOBILE_767}px)`]: {},
		},
	} as TypographyOptions,
	palette: {
		primary: {
			main: Colors.GREEN, //+
			light: Colors.GREY_LIGHT, //+
		},
		info: {
			main: Colors.BLUE_37, //+
			dark: Colors.BLUE_DARK, //+
			light: Colors.BLUE_LIGHT,
		},
		secondary: {
			main: Colors.PEOPLE,
			dark: Colors.PEOPLE_2, //+
		},
		warning: {
			main: Colors.ORANGE,
		},
		error: {
			main: Colors.RED, //+
			light: Colors.RED_LIGHT,
		},
		text: {
			primary: Colors.BLACK_OLD, //+
			secondary: Colors.GREY, //+
		},
		common: {
			black: Colors.BLACK, //+
			white: Colors.WHITE, //+
		},
	},
	components: {
		MuiTextField: {
			styleOverrides: {
				root: {
					"& .MuiOutlinedInput-root": {
						borderRadius: 8,
						backgroundColor: Colors.WHITE,
					},
				},
			},
		},
		MuiButton: {
			styleOverrides: {
				root: {
					borderRadius: 8,
					padding: "13px 22px",
					textTransform: "none",
					lineHeight: "22px",
					fontSize: "16px",

					[`@media (max-width: ${BreakPoints.FHD_1919}px)`]: {
						fontSize: "14px",
						fontWeight: 500,
						lineHeight: "21px",
						padding: "11px 20px",
					},
					[`@media (min-width: ${BreakPoints.TAPOK_319}px) and (max-width: ${BreakPoints.MOBILE_767}px)`]:
						{ padding: "10px 12px" },
				},
				outlinedInfo: {
					color: Colors.BLUE,
				},
				containedWarning: {
					color: Colors.WHITE,
				},
			},
		},
		MuiChip: {
			styleOverrides: {
				root: {
					padding: "7px 16px",
					height: "max-content",
					borderRadius: "100px",
					gap: "8px",
				},
				icon: {
					marginLeft: "0",
					marginRight: "0px",
					[`@media (max-width: ${BreakPoints.FHD_1919}px)`]: {
						width: "20px",
					},
				},
				label: {
					padding: "0",
				},
				colorSecondary: "red",
			},
		},
		MuiTypography: {
			styleOverrides: {
				root: {
					color: Colors.BLACK,
				},
			},
		},
		MuiCheckbox: {
			styleOverrides: {
				root: {
					color: Colors.PEOPLE_2,
					"&.Mui-checked": {
						color: Colors.PEOPLE_2,
					},
				},
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				input: {
					color: Colors.BLACK,
				},
			},
		},
	},
});

export const lightStyles = createTheme({
	typography: {
		fontFamily: "Montserrat !important",

		h4: {
			fontSize: "34px",
			fontWeight: 500,
			lineHeight: "41.82px",
			letterSpacing: "0.25px",
			[`@media (max-width: ${BreakPoints.MOBILE_767}px)`]: {
				fontSize: "24px",
			},
		},
		h5: {
			fontSize: "24px",
			fontWeight: 600,
			lineHeight: "31.92px",
			[`@media (max-width: ${BreakPoints.FHD_1919}px)`]: {
				fontSize: "20px",
				fontWeight: 500,
				lineHeight: "24px",
				letterSpacing: "0.15000000596046448px",
			},
			[`@media (max-width: ${BreakPoints.MOBILE_767}px)`]: {
				fontSize: "16px",
				fontWeight: 500,
				lineHeight: "24px",
				letterSpacing: "0.15000000596046448px",
			},
		},
		h6: {
			fontSize: "20px",
			fontWeight: 500,
			lineHeight: "32px",
			letterSpacing: "0.15px",
			[`@media (min-width: ${BreakPoints.TAPOK_319}px) and (max-width: ${BreakPoints.FHD_1919}px)`]:
				{
					fontSize: "14px",
					fontWeight: 500,
					lineHeight: "17px",
					letterSpacing: "0.10000000149011612px",
				},
		},

		body1: {
			fontSize: "16px",
			fontWeight: 500,
			lineHeight: "24px",
			letterSpacing: "0.15000000596046448px",
			[`@media (max-width: ${BreakPoints.FHD_1919}px)`]: {
				letterSpacing: "normal",
			},
			[`@media (max-width: ${BreakPoints.MOBILE_767}px)`]: {
				fontSize: "14px",
			},
		},
		body2: {
			fontSize: "14px",
			fontWeight: 400,
			lineHeight: "20.02px",
			letterSpacing: "0.17000000178813934px",
			[`@media (max-width: ${BreakPoints.FHD_1919}px)`]: {
				fontSize: "12px",
				fontWeight: 400,
				lineHeight: "19.92px",
				letterSpacing: "0.4000000059604645px",
			},
		},
		subtitle1: {
			fontSize: "16px",
			fontWeight: 400,
			lineHeight: "28px",
			letterSpacing: "0.15000000596046448px",
			[`@media (max-width: ${BreakPoints.FHD_1919}px)`]: {
				fontSize: "14px",
				fontWeight: 400,
				lineHeight: "21px",
			},
			[`@media (max-width: ${BreakPoints.MOBILE_767}px)`]: {
				fontSize: "12px",
				lineHeight: "18px",
			},
		},
		caption: {
			fontSize: "12px",
			fontWeight: 500,
			lineHeight: "19.92px",
			letterSpacing: "0.4000000059604645px",
		},
	},
	palette: {
		primary: {
			main: Colors.GREEN,
			light: Colors.GREY_LIGHT,
		},
		info: {
			main: Colors.BLUE,
			dark: Colors.BLUE_DARK,
			light: Colors.BLUE_LIGHT,
		},
		secondary: {
			main: Colors.PEOPLE,
			dark: Colors.PEOPLE_2,
		},
		warning: {
			main: Colors.ORANGE,
		},
		error: {
			main: Colors.RED,
			light: Colors.RED_LIGHT,
		},
		text: {
			primary: Colors.BLACK,
			secondary: Colors.GREY,
		},
		common: {
			black: Colors.BLACK,
			white: Colors.WHITE,
		},
	},
	components: {
		MuiTextField: {
			styleOverrides: {
				root: {
					"& .MuiOutlinedInput-root": {
						borderRadius: 8,
						backgroundColor: Colors.WHITE,
					},
				},
			},
		},
		MuiButton: {
			styleOverrides: {
				root: {
					borderRadius: 8,
					padding: "13px 22px",
					textTransform: "none",
					lineHeight: "22px",
					fontSize: "16px",

					[`@media (max-width: ${BreakPoints.FHD_1919}px)`]: {
						fontSize: "14px",
						fontWeight: 500,
						lineHeight: "21px",
						padding: "11px 20px",
					},
					[`@media (min-width: ${BreakPoints.TAPOK_319}px) and (max-width: ${BreakPoints.MOBILE_767}px)`]:
						{ padding: "10px 12px" },
				},
				outlinedInfo: {
					color: Colors.BLUE,
				},
				containedWarning: {
					color: Colors.WHITE,
				},
			},
		},
		MuiChip: {
			styleOverrides: {
				root: {
					padding: "7px 16px",
					height: "max-content",
					borderRadius: "100px",
					gap: "8px",
				},
				icon: {
					marginLeft: "0",
					marginRight: "0px",
					[`@media (max-width: ${BreakPoints.FHD_1919}px)`]: {
						width: "20px",
					},
				},
				label: {
					padding: "0",
				},
				colorSecondary: "red",
			},
		},
		MuiTypography: {
			styleOverrides: {
				root: {
					color: Colors.BLACK,
				},
			},
		},
		MuiCheckbox: {
			styleOverrides: {
				root: {
					color: Colors.PEOPLE_2,
					"&.Mui-checked": {
						color: Colors.PEOPLE_2,
					},
				},
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				input: {
					color: Colors.BLACK,
				},
			},
		},
	},
});
export const darkStyles = createTheme({
	typography: {
		fontFamily: "Montserrat !important",
	},
	palette: {
		primary: {
			main: Colors.RED,
		},
		error: {
			main: Colors.RED,
		},
	},
	components: {
		MuiTypography: {
			styleOverrides: {
				root: {
					color: Colors.WHITE,
				},
			},
		},
		MuiCard: {
			styleOverrides: {
				root: {
					backgroundColor: Colors.BLACK,
				},
			},
		},
	},
});
